/**
 * View router — URL-based routing.
 * Each top-level section has its own path.
 * Detail views (/interviews/:id, /articles/:id) are handled inside
 * the section component itself via useParams().
 */
import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import LoadingSpinner from './common/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import ReportsView from './reports/ReportsView';
import PodcastsView from './podcasts/PodcastsView';
import PressReleasesView from './press-releases/PressReleasesView';
import ManualReviewView from './manual-review/ManualReviewView';
import DeepQueryView from './deepquery/DeepQueryView';
import StoryClustersView from './deepquery/StoryClustersView';

const InterviewsView = lazy(() => import('./interviews/InterviewsView'));
const ListingsView = lazy(() => import('./listings/ListingsView'));
const NewListingPage = lazy(() => import('./listings/NewListingPage'));
const SourcesView = lazy(() => import('./sources/SourcesView'));
const ArticlesView   = lazy(() => import('./articles/ArticlesView'));
const SearchView     = lazy(() => import('./search/SearchView'));

const CSClientsView     = lazy(() => import('./content-studio/ClientsView'));
// MOCKUP — sample data only, no backend behind it.
const CSSourcesView     = lazy(() => import('./content-studio/CSSourcesView'));
const CSSubmissionsView = lazy(() => import('./content-studio/CSInterviewsView'));
const CSArticlesView    = lazy(() => import('./content-studio/ArticlesView'));
const CSSettingsView    = lazy(() => import('./content-studio/SettingsView'));
const CSDistributionView = lazy(() => import('./content-studio/DistributionView'));
const EditorialSettingsView = lazy(() => import('./settings/EditorialSettingsView'));
const AdminUsersView = lazy(() => import('./admin/UsersView'));
const AdminApiKeysView = lazy(() => import('./admin/ApiKeysView'));
const AdminActivityLogView = lazy(() => import('./admin/ActivityLogView'));
const AdminProductivityView = lazy(() => import('./admin/ProductivityView'));
const AdminPermissionsView = lazy(() => import('./admin/PermissionsView'));

const Fallback = () => (
  <div className="flex items-center justify-center h-full">
    <LoadingSpinner size="lg" text="Loading..." />
  </div>
);

const ViewRouter = () => {
  const { can } = useAuth();
  // Interview readers (editorial roles + journalists) land on Interviews;
  // Content Studio–only roles land in their own section.
  const home = can('interviews.view') ? '/interviews' : '/content-studio/articles';

  return (
  <Suspense fallback={<Fallback />}>
    <Routes>
      {/* Root → role-appropriate section */}
      <Route path="/" element={<Navigate to={home} replace />} />

      {/* Content Studio — separate client-work pipeline */}
      <Route path="/content-studio/clients"     element={<CSClientsView />} />
      <Route path="/content-studio/sources"     element={<CSSourcesView />} />
      <Route path="/content-studio/submissions" element={<CSSubmissionsView />} />
      <Route path="/content-studio/articles"    element={<CSArticlesView />} />
      <Route path="/content-studio/manual-submissions" element={<CSArticlesView queue="manual" />} />
      <Route path="/content-studio/distribution" element={<CSDistributionView />} />
      <Route path="/content-studio/settings"    element={<CSSettingsView />} />
      <Route path="/content-studio/listings"         element={<ListingsView team="content_studio" />} />
      <Route path="/content-studio/listings/new"     element={<NewListingPage team="content_studio" />} />
      <Route path="/content-studio/listings/:listingId" element={<ListingsView team="content_studio" />} />

      {/* Section routes — detail handled inside each view via useParams */}
      <Route path="/interviews"         element={<InterviewsView />} />
      <Route path="/interviews/:interviewId" element={<InterviewsView />} />

      <Route path="/listings"         element={<ListingsView team="editorial" />} />
      <Route path="/listings/new"     element={<NewListingPage team="editorial" />} />
      <Route path="/listings/:listingId" element={<ListingsView team="editorial" />} />

      {/* Sources — the person registry. Route params (not local state) so a Source is
          deep-linkable from an interview or article. */}
      <Route path="/sources"            element={<SourcesView />} />
      <Route path="/sources/:sourceId"  element={<SourcesView />} />

      <Route path="/articles"           element={<ArticlesView />} />
      <Route path="/articles/:articleId"    element={<ArticlesView />} />
      <Route path="/settings"           element={<EditorialSettingsView />} />

      <Route path="/search"             element={<SearchView />} />
      <Route path="/reports"            element={<ReportsView />} />
      <Route path="/press-releases"     element={<PressReleasesView />} />
      <Route path="/podcasts"           element={<PodcastsView />} />
      <Route path="/manual-review"      element={<ManualReviewView />} />
      <Route path="/deep-query"         element={<DeepQueryView />} />
      <Route path="/story-clusters"     element={<StoryClustersView />} />

      {/* Admin — super admin only (backend-enforced; nav hidden for others) */}
      <Route path="/admin/users"        element={<AdminUsersView />} />
      <Route path="/admin/api-keys"     element={<AdminApiKeysView />} />
      <Route path="/admin/activity"     element={<AdminActivityLogView />} />
      <Route path="/admin/productivity" element={<AdminProductivityView />} />
      <Route path="/admin/permissions"  element={<AdminPermissionsView />} />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to={home} replace />} />
    </Routes>
  </Suspense>
  );
};

export default ViewRouter;
